import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button, 
  CircularProgress, 
  Divider,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { noticeboard } from '../../utils/api';
import { useAuth } from '../../components/auth/AuthModule';
import NoticeList from './components/NoticeList';
import CreateNoticeModal from './components/CreateNoticeModal';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

const NoticeboardPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [notices, setNotices] = useState([]);
  const [displayedNotices, setDisplayedNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [sortMethod, setSortMethod] = useState('newest');

  useEffect(() => {
    const fetchNotices = async () => {
      setLoading(true);
      try {
        const response = await noticeboard.getAll();
        setNotices(response.notices || []);
      } catch (error) {
        console.error('Error fetching notices:', error);
        toast.error(t('common:errors.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, [t, refreshKey]);

  useEffect(() => {
    // Apply sorting to notices
    if (notices.length > 0) {
      let sortedNotices = [...notices];
      
      switch (sortMethod) {
        case 'newest':
          sortedNotices.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          break;
        case 'oldest':
          sortedNotices.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          break;
        case 'unread':
          sortedNotices.sort((a, b) => {
            const aRead = a.readBy.some(item => item.user._id === user._id);
            const bRead = b.readBy.some(item => item.user._id === user._id);
            if (aRead === bRead) {
              return new Date(b.createdAt) - new Date(a.createdAt); // Default to newest
            }
            return aRead ? 1 : -1; // Unread first
          });
          break;
        case 'read':
          sortedNotices.sort((a, b) => {
            const aRead = a.readBy.some(item => item.user._id === user._id);
            const bRead = b.readBy.some(item => item.user._id === user._id);
            if (aRead === bRead) {
              return new Date(b.createdAt) - new Date(a.createdAt); // Default to newest
            }
            return aRead ? -1 : 1; // Read first
          });
          break;
        default:
          break;
      }
      
      setDisplayedNotices(sortedNotices);
    } else {
      setDisplayedNotices([]);
    }
  }, [notices, sortMethod, user._id]);

  const handleRefresh = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const handleCreateNotice = async (noticeData) => {
    try {
      await noticeboard.create(noticeData);
      toast.success(t('common:success.created'));
      setOpenCreateModal(false);
      handleRefresh();
    } catch (error) {
      console.error('Error creating notice:', error);
      toast.error(t('common:errors.createFailed'));
    }
  };

  const handleUpdateNotice = async (id, data) => {
    try {
      await noticeboard.update(id, data);
      toast.success(t('common:success.updated'));
      handleRefresh();
    } catch (error) {
      console.error('Error updating notice:', error);
      toast.error(t('common:errors.updateFailed'));
    }
  };

  const handleDeleteNotice = async (id) => {
    try {
      await noticeboard.delete(id);
      toast.success(t('common:success.deleted'));
      handleRefresh();
    } catch (error) {
      console.error('Error deleting notice:', error);
      toast.error(t('common:errors.deleteFailed'));
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await noticeboard.markAsRead(id);
      toast.success(t('common:success.markedAsRead'));
      handleRefresh();
    } catch (error) {
      console.error('Error marking notice as read:', error);
      toast.error(t('common:errors.actionFailed'));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await noticeboard.markAllAsRead();
      
      if (response.count > 0) {
        toast.success(t('common:success.allMarkedAsRead', { count: response.count }));
      } else {
        toast.info(t('common:messages.noUnreadNotices'));
      }
      
      handleRefresh();
    } catch (error) {
      console.error('Error marking all notices as read:', error);
      toast.error(t('common:errors.actionFailed'));
    }
  };

  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortMethodChange = (method) => {
    setSortMethod(method);
    handleSortMenuClose();
  };

  return (
    <Container maxWidth="lg">
      <Box mt={2} mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" component="h1">
          {t('common:navigation.noticeboard')}
        </Typography>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton onClick={handleRefresh} color="primary" sx={{ mr: 1 }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={t('common:actions.markAllAsRead')}>
            <IconButton 
              onClick={handleMarkAllAsRead} 
              color="primary" 
              sx={{ mr: 1 }}
              disabled={loading || displayedNotices.length === 0 || !displayedNotices.some(notice => !notice.readBy.some(read => read.user._id === user._id))}
            >
              <CheckCircleOutlineIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={t('common:actions.sort')}>
            <IconButton 
              onClick={handleSortMenuOpen} 
              color="primary"
              sx={{ mr: 1 }}
            >
              <SortIcon />
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={sortMenuAnchor}
            open={Boolean(sortMenuAnchor)}
            onClose={handleSortMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem 
              onClick={() => handleSortMethodChange('newest')}
              selected={sortMethod === 'newest'}
            >
              <ListItemIcon>
                <ArrowDownwardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common:sort.newest')}</ListItemText>
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortMethodChange('oldest')}
              selected={sortMethod === 'oldest'}
            >
              <ListItemIcon>
                <ArrowUpwardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common:sort.oldest')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={() => handleSortMethodChange('unread')}
              selected={sortMethod === 'unread'}
            >
              <ListItemIcon>
                <PendingActionsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common:sort.unreadFirst')}</ListItemText>
            </MenuItem>
            <MenuItem 
              onClick={() => handleSortMethodChange('read')}
              selected={sortMethod === 'read'}
            >
              <ListItemIcon>
                <CheckCircleOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common:sort.readFirst')}</ListItemText>
            </MenuItem>
          </Menu>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateModal(true)}
          >
            {t('common:actions.create')}
          </Button>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <NoticeList 
          notices={displayedNotices} 
          currentUser={user} 
          onMarkAsRead={handleMarkAsRead}
          onUpdateNotice={handleUpdateNotice}
          onDeleteNotice={handleDeleteNotice}
        />
      )}

      <CreateNoticeModal 
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onCreateNotice={handleCreateNotice}
      />
    </Container>
  );
};

export default NoticeboardPage; 